import { Buildings, Check, CaretUpDown, SignOut, UserCircle } from '@phosphor-icons/react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/toaster';
import { AiAssistantButton } from '@/features/ai';
import { LegalLinks } from '@/features/legal/legal-links';
import { ImpersonationBanner } from '@/features/auth/impersonation-banner';
import { useSession } from '@/features/auth/session';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { SubscriptionBanner } from '@/features/subscription/banner';
import { SidebarNav } from './sidebar-nav';
import { Shell, TopBarFrame, UserMenuHeader, UserMenuTrigger } from './shell';

export function AppLayout() {
  return (
    <Shell
      menuTitle="Menu de navegação"
      menuDescription="Seções do sistema"
      banner={<ImpersonationBanner />}
      nav={(onNavigate) => <SidebarNav onNavigate={onNavigate} />}
      footer={(onNavigate) => (
        <>
          <AccountLink onNavigate={onNavigate} />
          <LegalLinks onNavigate={onNavigate} className="px-3 text-sidebar-muted" linkClassName="inline-flex min-h-8 items-center hover:text-white dark:hover:text-sidebar-foreground" />
        </>
      )}
      topBar={(onMenu) => <TopBar onMenu={onMenu} />}
      beforeMain={<SubscriptionBanner />}
    >
      <Outlet />
    </Shell>
  );
}

function AccountLink({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useSession();
  if (!me) return null;
  return (
    <NavLink
      to="/conta"
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'mt-auto flex min-h-12 items-center gap-3 rounded-control border-t border-sidebar-border px-3 pt-2 text-sm font-medium transition-colors',
          isActive ? 'text-white dark:text-sidebar-foreground' : 'text-sidebar-foreground/85 hover:text-white dark:hover:text-sidebar-foreground',
        )
      }
    >
      <UserCircle className="size-[18px] shrink-0 text-sidebar-muted" aria-hidden />
      <span className="min-w-0">
        <span className="block">Minha conta</span>
        <span className="block truncate text-xs font-normal text-sidebar-muted">{me.user.email}</span>
      </span>
    </NavLink>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const { me, membership, switchOrganization, logout } = useSession();
  const navigate = useNavigate();
  if (!me || !membership) return null;

  const onSwitch = async (id: string) => {
    if (id === membership.organizationId) return;
    try {
      await switchOrganization(id);
      toast.success('Loja alterada');
      navigate('/');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const onLogout = async () => {
    await logout();
    navigate('/entrar', { replace: true });
  };

  return (
    <TopBarFrame onMenu={onMenu}>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="min-w-0 max-w-[38vw] gap-2 sm:max-w-[60vw] px-2 sm:px-3" aria-label={`Loja ativa: ${membership.organizationName}. Trocar de loja`}>
            <span className="truncate font-display text-base font-semibold">{membership.organizationName}</span>
            {me.memberships.length > 1 && <CaretUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
          </Button>
        </DropdownMenuTrigger>
        {me.memberships.length > 1 && (
          <DropdownMenuContent align="start" className="min-w-64">
            <DropdownMenuLabel>Trocar de loja</DropdownMenuLabel>
            {me.memberships.map((m) => (
              <DropdownMenuItem key={m.organizationId} onSelect={() => void onSwitch(m.organizationId)}>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{m.organizationName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{m.roleName}</span>
                </span>
                {m.organizationId === membership.organizationId && <Check className="size-4 text-primary" weight="bold" aria-label="Ativa" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>

      <div className="ml-auto" />
      <AiAssistantButton />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <UserMenuTrigger name={me.user.name} initialsText={initials(me.user.name)} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 max-w-[calc(100vw-1rem)]">
          <UserMenuHeader name={me.user.name} email={me.user.email} role={membership.roleName} />
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/conta">
              <UserCircle className="size-4" aria-hidden />
              Minha conta
            </Link>
          </DropdownMenuItem>
          {me.platformRole && (
            <DropdownMenuItem asChild>
              <Link to="/plataforma" className="text-muted-foreground">
                <Buildings className="size-4" aria-hidden />
                Plataforma
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => void onLogout()}>
            <SignOut className="size-4" aria-hidden />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TopBarFrame>
  );
}
